import { CapacitorCookies } from '@capacitor/core';
import { useCapacitorPreferences } from '@/composables/capacitor/useCapacitorPreferences';
import type { CookieConfig, TokenStorageStrategy } from '../tokenContracts';

const REFRESH_TOKEN_EXPIRY_DAYS = 30;
const PREFS_ACCESS_TOKEN_KEY = 'cap_access_token';
const PREFS_REFRESH_TOKEN_KEY = 'cap_refresh_token';
const { getPreference, setPreference } = useCapacitorPreferences();

export class CapacitorStrategy implements TokenStorageStrategy {
  private readonly cache = new Map<string, string>();

  constructor(private readonly cookieUrl: string) {}

  async initialize(config: CookieConfig): Promise<void> {
    try {
      const [access, refresh] = await Promise.all([
        getPreference(PREFS_ACCESS_TOKEN_KEY),
        getPreference(PREFS_REFRESH_TOKEN_KEY)
      ]);

      if (access) {
        this.cache.set(config.accessTokenName, access);
        // Restore to native cookie jar so CapacitorHttp sends it with API requests
        CapacitorCookies.setCookie({
          url: this.cookieUrl,
          key: config.accessTokenName,
          value: access,
          path: config.path
        }).catch(err => console.error('[CapacitorStrategy] restore access cookie failed:', err));
      }
      if (refresh) {
        this.cache.set(config.refreshTokenName, refresh);
        CapacitorCookies.setCookie({
          url: this.cookieUrl,
          key: config.refreshTokenName,
          value: refresh,
          path: config.path
        }).catch(err => console.error('[CapacitorStrategy] restore refresh cookie failed:', err));
      }
    } catch (err) {
      console.error('[CapacitorStrategy] initialize failed, cache will be empty:', err);
    }
  }

  setAccessToken(token: string, expiresAt: Date, config: CookieConfig): void {
    this.cache.set(config.accessTokenName, token);
    setPreference(PREFS_ACCESS_TOKEN_KEY, token).catch(err =>
      console.error('[CapacitorStrategy] setAccessToken (prefs) failed:', err)
    );
    CapacitorCookies.setCookie({
      url: this.cookieUrl,
      key: config.accessTokenName,
      value: token,
      expires: expiresAt.toUTCString(),
      path: config.path
    }).catch(err => console.error('[CapacitorStrategy] setAccessToken (cookie) failed:', err));
  }

  getAccessToken(config: CookieConfig): string | undefined {
    return this.cache.get(config.accessTokenName);
  }

  setRefreshToken(token: string, config: CookieConfig): void {
    this.cache.set(config.refreshTokenName, token);
    setPreference(PREFS_REFRESH_TOKEN_KEY, token).catch(err =>
      console.error('[CapacitorStrategy] setRefreshToken (prefs) failed:', err)
    );
    const expires = new Date();
    expires.setDate(expires.getDate() + REFRESH_TOKEN_EXPIRY_DAYS);
    CapacitorCookies.setCookie({
      url: this.cookieUrl,
      key: config.refreshTokenName,
      value: token,
      expires: expires.toUTCString(),
      path: config.path
    }).catch(err => console.error('[CapacitorStrategy] setRefreshToken (cookie) failed:', err));
  }

  getRefreshToken(config: CookieConfig): string | undefined {
    return this.cache.get(config.refreshTokenName);
  }

  clearAll(config: CookieConfig): void {
    this.cache.clear();
    setPreference(PREFS_ACCESS_TOKEN_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (prefs access) failed:', err)
    );
    setPreference(PREFS_REFRESH_TOKEN_KEY).catch(err =>
      console.error('[CapacitorStrategy] clearAll (prefs refresh) failed:', err)
    );
    CapacitorCookies.deleteCookie({ url: this.cookieUrl, key: config.accessTokenName }).catch(err =>
      console.error('[CapacitorStrategy] deleteCookie(access) failed:', err)
    );
    CapacitorCookies.deleteCookie({ url: this.cookieUrl, key: config.refreshTokenName }).catch(
      err => console.error('[CapacitorStrategy] deleteCookie(refresh) failed:', err)
    );
  }
}
